import { useEffect, useState, type RefObject } from 'react';

/** Tolerance for the fractional pixel a scrolled-to-the-end box can be left with. */
const END_TOLERANCE_PX = 1;

export interface ScrollMetrics {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

/** Whether anything is still below the visible part of a scrolling box. */
export function hasMoreBelow({
  scrollTop,
  clientHeight,
  scrollHeight,
}: ScrollMetrics): boolean {
  return scrollHeight - (scrollTop + clientHeight) > END_TOLERANCE_PX;
}

/**
 * Tracks whether a scrolling region still has content below the fold.
 *
 * The sidebar hides its scrollbars — on Windows they are drawn with arrow
 * buttons and stepper tracks, and two of them nested read as a rendering
 * fault rather than as an affordance — so something else has to say "there is
 * more". A fade at the bottom edge does that, and it has to go out when the
 * end is reached or it becomes a permanent decoration that says nothing.
 *
 * Watches three things because three different events change the answer: the
 * reader scrolling, the box being resized, and content arriving (boards land
 * asynchronously, changing `scrollHeight` without either of the others).
 */
export function useHasMoreBelow(ref: RefObject<HTMLElement | null>): boolean {
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () => {
      setMoreBelow(hasMoreBelow(element));
    };
    update();

    element.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(element);
    const content = new MutationObserver(update);
    content.observe(element, { childList: true, subtree: true });

    return () => {
      element.removeEventListener('scroll', update);
      resize.disconnect();
      content.disconnect();
    };
  }, [ref]);

  return moreBelow;
}
